import type {
	Business,
	List,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';
import { dashboardOpenEmailPreview } from '@/modules/dashboard/dashboardActions';
import {
	Checkmark,
	CheckmarkIcon,
	CheckmarkIconBlack,
	DropDownGold,
	EmailContactIcon,
	FlagIcon,
	Instagram,
	Messenger,
	PhoneContactIcon,
	PlusGold,
	YelpStar,
	YelpStarEmpty,
	Trash,
	XIcon,
	XIconBlack,
	Yelp,
	YelpIcon,
	Website,
	Google,
	Clock,
	ExclamationMarkRedUnfilled,
	ScheduleIconNoClock,
	ExclamationMarkOctagon,
	InfoWhite,
	Map
} from '@/assets/images';
import Twitter from '@/assets/images/twitter-logo.png';
import Youtube from '@/assets/images/youtube-transparant.png';
import Pinterest from '@/assets/images/pinterest-transparant.png';
import Snapchat from '@/assets/images/snapchat-transparant.png';
import Tiktok from '@/assets/images/tiktok-transparant.png';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import encodeUrl from '@/utils/encodeUrl';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import CopyToClipboard from '@/components/CopyToClipboard/CopyToClipboard';
import Button from '@/components/Button/newButtons';
import { Tooltip } from '@/components/atoms/Tooltip';
import type { DirectEmail } from '@/modules/email/emailTypes';
import { UserRole } from '@/modules/user/userTypes';

interface Props {
	selectBusiness: Business;
	openSetReminder: () => void;
	openAddDataModal: () => void;
	toggleContactedUserData: (i: number) => void;
	toggleContacted: (type: string) => void;
	openComposeEmail: (
		business: DirectEmail['business'],
		email: DirectEmail['email']
	) => void;
	selectedList?: List;
	toggleLead: (e: boolean) => void;
	saveNote?: (businessId: string, notes: UserNotes) => void;
	openDeleteDataModal: (x: number) => void;
	openReportIncorrectDataModal: (x: string) => void;
	goToMap?: (businessId: string) => void;
	parentNodeName?: string;
}

const BusinessInfoTab = ({
	selectBusiness,
	openSetReminder,
	openAddDataModal,
	toggleContactedUserData,
	toggleContacted,
	openComposeEmail,
	selectedList,
	toggleLead,
	parentNodeName,
	saveNote,
	openDeleteDataModal,
	goToMap
}: Props) => {
	const [user, scheduledEmailActivity, query] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
			state.dashboard.getIn(['query']).toJS()
		],
		shallowEqual
	);

	const dispatch = useDispatch();

	const openEmailPreview = () => dispatch(dashboardOpenEmailPreview());

	const toggleHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName('hours-holder')[0];

		if (hoursEl && hoursEl.classList.contains('hours-opened')) {
			closeHoursDropdown();
		} else {
			openHoursDropdown();
		}
	};

	const openHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName('hours-holder')[0];
		const hoursChev = document.getElementsByClassName('down-chevron')[0];

		if (!hoursEl.classList.contains('hours-opened')) {
			hoursEl.classList.add('hours-opened');
			hoursChev.classList.add('flipped');
		}
	};

	const closeHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName('hours-holder')[0];
		const hoursChev = document.getElementsByClassName('down-chevron')[0];

		if (hoursEl && hoursEl.classList.contains('hours-opened')) {
			hoursEl.classList.remove('hours-opened');
			hoursChev.classList.remove('flipped');
		}
	};

	const [hideLeadSuggestion, setHideLeadSuggestion] = useState(false);
	const [showAboutMore, setShowAboutMore] = useState(false);

	useEffect(() => {
		setHideLeadSuggestion(false);
		closeHoursDropdown();
	}, [selectBusiness?.id, selectBusiness?._id]);

	const toggleAbout = () => {
		if (showAboutMore) {
			setShowAboutMore(false);
		} else {
			setShowAboutMore(true);
		}
	};

	const [leadUpdate, setLeadUpdate] = useState(false);

	const getContactedOverOneHour = useCallback(() => {
		if (
			selectBusiness.lastContact &&
			Object.keys(selectBusiness.lastContact).length > 0
		) {
			let temp = 0;

			Object.keys(selectBusiness.lastContact).forEach(key => {
				const epochDate = new Date(
					selectBusiness.lastContact[key]['date']
				).valueOf();

				if (temp < epochDate) {
					temp = epochDate;
				}
			});

			if (temp > 0) {
				const lastDate = new Date(temp).valueOf();

				const curr = new Date().getTime();

				const diff =
					parseInt(String(curr), 10) - parseInt(String(lastDate), 10);

				return diff / 1000 / 60 / 60 > 1;
			}
			return false;
		}

		return false;
	}, [selectBusiness.lastContact]);

	const contactNameCleanUp = (inputName: string) => {
		return inputName
			.toLowerCase()
			.replace(/\b\w/g, firstLetter => firstLetter.toUpperCase());
	};

	const contactTitleCleanUp = (inputArray: Array<string>) => {
		const valuesToRemove = ['contact name']; // Known values to remove

		const returnArray = [];

		for (let i = inputArray.length - 1; i >= 0; i--) {
			if (valuesToRemove.includes(inputArray[i].toLowerCase())) {
				inputArray.splice(i, 1);
			} else {
				returnArray.push(
					inputArray[i].charAt(0).toUpperCase() +
						inputArray[i].slice(1).toLowerCase()
				);
			}
		}

		return returnArray;
	};

	const getDateIncorporated = (yearsInBusiness: number) => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		return currentYear - yearsInBusiness;
	};

	const [isReportDataModalOpen, setIsReportDataModalOpen] = useState(false);

	const handleCloseReportDataModal = () => {
		setIsReportDataModalOpen(false);
	};

	const showLeadSuggestion = useMemo(() => {
		if (
			selectBusiness.isContacted &&
			(!selectBusiness.lead || leadUpdate) &&
			(selectBusiness.lead !== false || leadUpdate) &&
			!hideLeadSuggestion &&
			getContactedOverOneHour()
		) {
			return true;
		}
		return false;
	}, [
		getContactedOverOneHour,
		hideLeadSuggestion,
		leadUpdate,
		selectBusiness.isContacted,
		selectBusiness.lead
	]);

	const [showLeadFollowUp, setShowLeadFollowUp] = useState(false);

	const activateLeadButton = (
		buttonEl: HTMLButtonElement,
		markedAsLead: boolean
	) => {
		buttonEl.classList.add('lead-button-activated');

		setLeadUpdate(true);

		if (markedAsLead) {
			setTimeout(() => {
				buttonEl.classList.remove('lead-button-activated');
				setShowLeadFollowUp(true);
				setHideLeadSuggestion(true);
				setLeadUpdate(false);
			}, 2000);

			setTimeout(() => {
				setShowLeadFollowUp(false);
			}, 12000);
		} else {
			setTimeout(() => {
				buttonEl.classList.remove('lead-button-activated');
				setShowLeadFollowUp(true);
				setHideLeadSuggestion(true);
				setLeadUpdate(false);
			}, 2000);

			setTimeout(() => {
				setShowLeadFollowUp(false);
			}, 12000);
		}
	};

	const hideAllLeadBoxes = () => {
		setHideLeadSuggestion(true);
		setShowLeadFollowUp(false);
	};

	const addLinkAnalytics = (type: string) => {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User contacted a business via their: ${type}`,
			properties: {
				businessName: selectBusiness.name,
				businessId: selectBusiness.businessId || selectBusiness._id,
				businessAddress: selectBusiness.address
			}
		});
	};

	const searchTerm = () => {
		const term = `${selectBusiness.name} ${selectBusiness?.address?.city} ${selectBusiness?.address?.state}`;
		return term.replace('&', '%26');
	};

	const instagramRegex = (instagramUrl: string) => {
		const igRegex =
			/^https?:\/\/(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)/;

		const match = instagramUrl.match(igRegex);

		if (match && match[1]) {
			const username = match[1];
			const directUrl = `https://ig.me/m/${username}`;
			return directUrl;
		}

		return instagramUrl;
	};

	if (!selectBusiness) return null;

	return (
		<div className="info-tab-holder">
			{showLeadSuggestion && (
				<div className="info-tab-lead-suggestion-box">
					<div className="info-tab-lead-text-box">
						<div className="info-tab-lead-title">
							Did this contact result in a lead?
						</div>

						{selectedList && (
							<div className="info-tab-lead-list-name">
								{selectedList.name}
							</div>
						)}
					</div>

					<div className="info-tab-lead-buttons-box">
						<button
							type="button"
							className="info-tab-lead-button"
							onClick={evt => {
								activateLeadButton(evt.currentTarget, true);
								toggleLead(true);
							}}
						>
							<CheckmarkIcon className="info-tab-lead-button-check info-tab-lead-button-check-white" />
							<CheckmarkIconBlack className="info-tab-lead-button-check info-tab-lead-button-check-black" />
							<span>Yes</span>
						</button>

						<button
							type="button"
							className="info-tab-lead-button info-tab-lead-no-button"
							onClick={evt => {
								activateLeadButton(evt.currentTarget, false);
								toggleLead(false);
							}}
						>
							<XIcon className="info-tab-lead-button-check info-tab-lead-button-check-white" />
							<XIconBlack className="info-tab-lead-button-check info-tab-lead-button-check-black" />
							<span>No</span>
						</button>

						<button
							type="button"
							className="info-tab-lead-ask-later"
							onClick={() => {
								setHideLeadSuggestion(true);
							}}
						>
							Ask me later
						</button>
					</div>

					<div className="info-tab-lead-background" />
				</div>
			)}

			{showLeadFollowUp && selectBusiness.lead === true && (
				<div className="lead-follow-up-holder">
					<div className="lead-follow-up-holder-bg" />

					<div className="lead-follow-up-checkholder">
						<Checkmark className="lead-follow-up-check" />
					</div>

					<div className="lead-follow-up-title">Marked as Lead</div>

					<button
						type="button"
						className="lead-follow-up-undo"
						onClick={() => {
							toggleLead(false);
							hideAllLeadBoxes();
						}}
					>
						Undo this action
					</button>
				</div>
			)}

			{showLeadFollowUp && selectBusiness.lead === false && (
				<div className="lead-follow-up-holder">
					<div className="lead-follow-up-holder-bg" />

					<div className="lead-follow-up-checkholder-negative">
						<XIconBlack className="lead-follow-up-x" />
					</div>

					<div className="lead-follow-up-title">
						Marked as not a Lead
					</div>

					<button
						type="button"
						className="lead-follow-up-undo"
						onClick={() => {
							toggleLead(false);
							hideAllLeadBoxes();
						}}
					>
						Undo this action
					</button>
				</div>
			)}

			<div className="info-tab-photo-holder">
				{selectBusiness?.yelp?.thumnnailURL ? (
					<img
						className="info-tab-photo"
						src={selectBusiness?.yelp?.thumnnailURL}
						alt="Yelp thumbnail"
					/>
				) : (
					<div
						className={`no-photo-placeholder ${!showLeadSuggestion && !showLeadFollowUp ? 'no-lead-popup' : ''}`}
					/>
				)}
			</div>

			<div className="info-tab-body-holder">
				<div className="info-tab-yelp-reminder-wrapper">
					<button
						type="button"
						className="info-tab-reminders-button"
						onClick={openSetReminder}
					>
						{selectBusiness?.reminder?.hasReminderTaskSet ? (
							<span>See Your Reminder</span>
						) : (
							<>
								<FlagIcon className="reminder-flag-icon" />
								<span>Set Reminder</span>
							</>
						)}
					</button>

					<div className="info-tab-yelp-holder">
						{selectBusiness.yelp?.reviews ? (
							<>
								<div className="yelp-stars-holder">
									<YelpIcon className="info-tab-yelp-icon" />

									{!selectBusiness?.yelp?.rating ||
									selectBusiness.yelp.rating === 0 ? (
										<div className="yelp-no-rating">
											No Yelp reviews
										</div>
									) : (
										<>
											{selectBusiness?.yelp?.rating >
											0 ? (
												<YelpStar className="info-tab-yelp-star" />
											) : (
												<YelpStarEmpty className="info-tab-yelp-star" />
											)}

											{selectBusiness?.yelp?.rating >
											1 ? (
												<YelpStar className="info-tab-yelp-star" />
											) : (
												<YelpStarEmpty className="info-tab-yelp-star" />
											)}

											{selectBusiness?.yelp?.rating >
											2 ? (
												<YelpStar className="info-tab-yelp-star" />
											) : (
												<YelpStarEmpty className="info-tab-yelp-star" />
											)}

											{selectBusiness?.yelp?.rating >
											3 ? (
												<YelpStar className="info-tab-yelp-star" />
											) : (
												<YelpStarEmpty className="info-tab-yelp-star" />
											)}

											{selectBusiness?.yelp?.rating >
											4 ? (
												<YelpStar className="info-tab-yelp-star" />
											) : (
												<YelpStarEmpty className="info-tab-yelp-star" />
											)}
										</>
									)}
								</div>

								{selectBusiness?.yelp?.rating > 0 && (
									<div className="yelp-number-of-reviews">
										{selectBusiness.yelp.reviews} Review
										{selectBusiness.yelp.reviews === 0
											? ''
											: 's'}
									</div>
								)}
							</>
						) : (
							<>
								<YelpIcon className="info-tab-yelp-icon" />

								<div className="yelp-no-rating">
									No Yelp reviews
								</div>
							</>
						)}
					</div>
				</div>

				<div className="info-tab-communications">
					<div className="info-tab-section-title">Communication</div>

					<div className="info-tab-email-holder">
						{selectBusiness &&
							selectBusiness.email &&
							selectBusiness.emailLowPriority !== true && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.emailSecondary ? 'switch-contacted' : ''}`}
								>
									<EmailContactIcon className="info-tab-contact-icon info-tab-contact-icon-email" />

									<Tooltip.Provider>
										<Tooltip.Root>
											<Tooltip.Trigger asChild>
												<button
													type="button"
													className={`info-tab-contact-content  w-full overflow-hidden text-ellipsis whitespace-nowrap text-start ${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
													onClick={() => {
														openComposeEmail(
															selectBusiness,
															selectBusiness.email
														);
													}}
												>
													{selectBusiness.email}
												</button>
											</Tooltip.Trigger>

											<Tooltip.Content>
												{
													user.role === UserRole.Free || user.role === UserRole.PreTrial ? 
														'Upgrade to a paid user to uncover email addresses' : 
														selectBusiness.email
												}
											</Tooltip.Content>
										</Tooltip.Root>
									</Tooltip.Provider>

									{selectBusiness.emailStatus === 'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<span className="info-tab-email-queued">
												<Clock className="info-tab-email-queued-icon" />
												Queued to send
											</span>
										)}

									{selectBusiness.emailStatus ===
										'failed' && (
										<span className="info-tab-email-failed">
											<ExclamationMarkRedUnfilled className="info-tab-email-failed-icon" />
											Failed to send&nbsp;
											<button
												type="button"
												className="info-tab-email-failed-retry"
												onClick={() => {
													openComposeEmail(
														selectBusiness,
														selectBusiness.email
													);
												}}
											>
												Retry
											</button>
										</span>
									)}

									{selectBusiness.emailStatus !== 'failed' &&
										selectBusiness.emailStatus !==
											'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<button
												type="button"
												className="info-tab-contacted-switch"
												onClick={() => {
													toggleContacted(
														'emailSecondary'
													);
												}}
											>
												<Checkmark className="info-tab-contact-check" />{' '}
												<span
													className="info-tab-contact-checked"
													id="info-tab-contact-check"
												>
													Contacted
												</span>
												<span
													className="info-tab-contact-not-checked"
													id="info-tab-contact-unchecked"
												>
													Mark contacted
												</span>
											</button>
										)}

									{scheduledEmailActivity[
										`${selectBusiness._id}`
									] && (
										<button
											type="button"
											className="info-tab-scheduled-send"
											onClick={openEmailPreview}
										>
											<ScheduleIconNoClock /> Scheduled
										</button>
									)}
								</div>
							)}

						{selectBusiness &&
							selectBusiness.facebook &&
							selectBusiness.facebook.email &&
							selectBusiness.facebook.emailLowPriority !== true &&
							selectBusiness.facebook.email !==
								selectBusiness.email && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.emailPrimary ? 'switch-contacted' : ''}`}
								>
									<EmailContactIcon className="info-tab-contact-icon info-tab-contact-icon-email" />

									<Tooltip.Provider>
										<Tooltip.Root>
											<Tooltip.Trigger asChild>
												<button
													type="button"
													className={`info-tab-contact-content w-full overflow-hidden text-ellipsis whitespace-nowrap text-start ${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
													onClick={() => {
														openComposeEmail(
															selectBusiness,
															selectBusiness
																.facebook
																?.email || ''
														);
													}}
												>
													{selectBusiness.facebook
														?.email || ''}
												</button>
											</Tooltip.Trigger>

											<Tooltip.Content>
												{
													user.role === UserRole.Free || user.role === UserRole.PreTrial ? 
														'Upgrade to a paid user to uncover email addresses' :
														selectBusiness.facebook?.email || ''
												}
											</Tooltip.Content>
										</Tooltip.Root>
									</Tooltip.Provider>

									{selectBusiness.emailStatus === 'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<span className="info-tab-email-queued">
												<Clock className="info-tab-email-queued-icon" />
												Queued to send
											</span>
										)}

									{selectBusiness.emailStatus ===
										'failed' && (
										<span className="info-tab-email-failed">
											<ExclamationMarkRedUnfilled className="info-tab-email-failed-icon" />
											Failed to send&nbsp;
											<button
												type="button"
												className="info-tab-email-failed-retry"
												onClick={() => {
													openComposeEmail(
														selectBusiness,
														selectBusiness.facebook
															?.email || ''
													);
												}}
											>
												Retry
											</button>
										</span>
									)}

									{selectBusiness.emailStatus !== 'failed' &&
										selectBusiness.emailStatus !==
											'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<button
												type="button"
												onClick={() => {
													toggleContacted(
														'emailPrimary'
													);
												}}
												className="info-tab-contacted-switch"
											>
												<Checkmark className="info-tab-contact-check" />{' '}
												<span
													className="info-tab-contact-checked"
													id="info-tab-contact-check"
												>
													Contacted
												</span>
												<span
													className="info-tab-contact-not-checked"
													id="info-tab-contact-unchecked"
												>
													Mark contacted
												</span>
											</button>
										)}

									{scheduledEmailActivity[
										`${selectBusiness._id}`
									] && (
										<button
											type="button"
											className="info-tab-scheduled-send"
											onClick={openEmailPreview}
										>
											<ScheduleIconNoClock /> Scheduled
										</button>
									)}
								</div>
							)}

						{selectBusiness &&
							selectBusiness.web &&
							selectBusiness.web.email &&
							selectBusiness.web.email !== selectBusiness.email &&
							selectBusiness.web.emailLowPriority !== true &&
							selectBusiness.web.email !==
								selectBusiness.facebook?.email && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.thirdEmail ? 'switch-contacted' : ''}`}
								>
									<EmailContactIcon className="info-tab-contact-icon info-tab-contact-icon-email" />

									<Tooltip.Provider>
										<Tooltip.Root>
											<Tooltip.Trigger asChild>
												<button
													type="button"
													className={`info-tab-contact-content w-full overflow-hidden text-ellipsis whitespace-nowrap text-start ${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
													onClick={() => {
														openComposeEmail(
															selectBusiness,
															selectBusiness.web
																.email
														);
													}}
												>
													{selectBusiness.web.email}
												</button>
											</Tooltip.Trigger>

											<Tooltip.Content>
												{
													user.role === UserRole.Free || user.role === UserRole.PreTrial ? 
														'Upgrade to a paid user to uncover email addresses' : 
														selectBusiness.web.email
												}
											</Tooltip.Content>
										</Tooltip.Root>
									</Tooltip.Provider>

									{selectBusiness.emailStatus === 'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<span className="info-tab-email-queued">
												<Clock className="info-tab-email-queued-icon" />
												Queued to send
											</span>
										)}

									{selectBusiness.emailStatus ===
										'failed' && (
										<span className="info-tab-email-failed">
											<ExclamationMarkRedUnfilled className="info-tab-email-failed-icon" />
											Failed to send&nbsp;
											<button
												type="button"
												className="info-tab-email-failed-retry"
												onClick={() => {
													openComposeEmail(
														selectBusiness,
														selectBusiness.web.email
													);
												}}
											>
												Retry
											</button>
										</span>
									)}

									{selectBusiness.emailStatus !== 'failed' &&
										selectBusiness.emailStatus !==
											'queued' &&
										!scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<button
												type="button"
												className="info-tab-contacted-switch"
												onClick={() => {
													toggleContacted(
														'thirdEmail'
													);
												}}
											>
												<Checkmark className="info-tab-contact-check" />{' '}
												<span
													className="info-tab-contact-checked"
													id="info-tab-contact-check"
												>
													Contacted
												</span>
												<span className="info-tab-contact-not-checked">
													Mark contacted
												</span>
											</button>
										)}

									{scheduledEmailActivity[
										`${selectBusiness._id}`
									] && (
										<button
											type="button"
											className="info-tab-scheduled-send"
											onClick={openEmailPreview}
										>
											<ScheduleIconNoClock /> Scheduled
										</button>
									)}
								</div>
							)}

						{selectBusiness &&
							selectBusiness.userAddedData &&
							selectBusiness.userAddedData.map((data, ind) => {
								if (data.type !== 'email') return null;

								return (
									<div
										className={`info-tab-contact-type-holder gap-3 ${data.checked ? 'switch-contacted' : ''}`}
										key={data.data}
									>
										<EmailContactIcon className="info-tab-contact-icon info-tab-contact-icon-email" />

										<Tooltip.Provider>
											<Tooltip.Root>
												<Tooltip.Trigger asChild>
													<button
														type="button"
														className="info-tab-contact-content w-full overflow-hidden text-ellipsis whitespace-nowrap text-start"
														onClick={() => {
															openComposeEmail(
																selectBusiness,
																data.data
															);
														}}
													>
														{data.data}
													</button>
												</Tooltip.Trigger>

												<Tooltip.Content>
													{data.data}
												</Tooltip.Content>
											</Tooltip.Root>
										</Tooltip.Provider>

										<Trash
											id="info-tab-email-trash"
											className="info-tab-contact-trash cursor-pointer"
											onClick={() => {
												openDeleteDataModal(ind);
											}}
										/>

										{selectBusiness.emailStatus ===
											'queued' &&
											!scheduledEmailActivity[
												`${selectBusiness._id}`
											] && (
												<span className="info-tab-email-queued">
													<Clock className="info-tab-email-queued-icon" />
													Queued to send
												</span>
											)}

										{selectBusiness.emailStatus !==
											'queued' &&
											(selectBusiness.emailStatus !==
												'failed' &&
											!scheduledEmailActivity[
												`${selectBusiness._id}`
											] ? (
												<button
													type="button"
													className="info-tab-contacted-switch"
													onClick={() => {
														toggleContactedUserData(
															ind
														);
													}}
												>
													<Checkmark className="info-tab-contact-check" />{' '}
													<span
														className="info-tab-contact-checked"
														id="info-tab-contact-check"
													>
														Contacted
													</span>
													<span
														className="info-tab-contact-not-checked"
														id="info-tab-contact-unchecked"
													>
														Mark contacted
													</span>
												</button>
											) : (
												<span className="info-tab-email-failed">
													<ExclamationMarkRedUnfilled className="info-tab-email-failed-icon" />
													Failed to send&nbsp;
													<button
														type="button"
														className="info-tab-email-failed-retry"
														onClick={() => {
															openComposeEmail(
																selectBusiness,
																data.data
															);
														}}
													>
														Retry
													</button>
												</span>
											))}

										{scheduledEmailActivity[
											`${selectBusiness._id}`
										] && (
											<button
												type="button"
												className="info-tab-scheduled-send"
												onClick={openEmailPreview}
											>
												<ScheduleIconNoClock />{' '}
												Scheduled
											</button>
										)}
									</div>
								);
							})}

						{selectBusiness &&
							selectBusiness.facebook &&
							selectBusiness.facebook.messengerUrl && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.messenger ? 'switch-contacted' : ''}`}
								>
									<Messenger className="info-tab-contact-icon info-tab-contact-icon-messenger" />
									<a
										href={encodeUrl(
											`https://${selectBusiness?.facebook?.messengerUrl}`
										)}
										target="_blank"
										onClick={() => {
											toggleContacted('messenger');
										}}
										rel="noopener noreferrer"
									>
										<div className="info-tab-contact-content">
											Facebook Messenger
										</div>
									</a>

									<button
										type="button"
										className="info-tab-contacted-switch"
										onClick={() => {
											toggleContacted('messenger');
										}}
									>
										<Checkmark className="info-tab-contact-check" />{' '}
										<span
											id="fb-msg-contact-checked"
											className="info-tab-contact-checked"
										>
											Contacted
										</span>
										<span
											id="fb-msg-contact-unchecked"
											className="info-tab-contact-not-checked"
										>
											Mark contacted
										</span>
									</button>
								</div>
							)}

						{selectBusiness &&
							selectBusiness.socials &&
							selectBusiness.socials.instagram && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.instagramMessenger ? 'switch-contacted' : ''}`}
								>
									<Instagram className="info-tab-contact-icon info-tab-contact-icon-messenger" />

									<a
										href={encodeUrl(
											`${instagramRegex(selectBusiness.socials.instagram)}`
										)}
										target="_blank"
										onClick={() => {
											toggleContacted(
												'instagramMessenger'
											);
										}}
										rel="noopener noreferrer"
									>
										<div className="info-tab-contact-content">
											Instagram Messenger
										</div>
									</a>

									<button
										type="button"
										className="info-tab-contacted-switch"
										onClick={() => {
											toggleContacted(
												'instagramMessenger'
											);
										}}
									>
										<Checkmark className="info-tab-contact-check" />{' '}
										<span className="info-tab-contact-checked">
											Contacted
										</span>
										<span className="info-tab-contact-not-checked">
											Mark contacted
										</span>
									</button>
								</div>
							)}

						{selectBusiness &&
							selectBusiness.phone &&
							selectBusiness.phone?.phone && (
								<div
									className={`info-tab-contact-type-holder gap-3 ${selectBusiness.contactType?.phone ? 'switch-contacted' : ''}`}
								>
									<PhoneContactIcon className="info-tab-contact-icon info-tab-contact-icon-phone" />
									<a
										href={`tel:${selectBusiness.phone.display}`}
										target="_blank"
										onClick={() => {
											toggleContacted('phone');
										}}
										rel="noopener noreferrer"
									>
										<div className="info-tab-contact-content">
											{selectBusiness.phone.display}
										</div>
									</a>
									<div className="my-4 ml-4 inline-block border-2 border-transparent">
										<CopyToClipboard
											textToCopy={
												selectBusiness.phone.display
											}
											type="Phone number"
										/>
									</div>
									<button
										type="button"
										className="info-tab-contacted-switch"
										onClick={() => {
											toggleContacted('phone');
										}}
									>
										<Checkmark className="info-tab-contact-check" />{' '}
										<span className="info-tab-contact-checked">
											Contacted
										</span>
										<span className="info-tab-contact-not-checked">
											Mark contacted
										</span>
									</button>
								</div>
							)}

						{selectBusiness &&
							selectBusiness.userAddedData &&
							selectBusiness.userAddedData.map((data, ind) => {
								if (data.type !== 'phone') return null;

								return (
									<div
										className={`info-tab-contact-type-holder gap-3 ${data.checked ? 'switch-contacted' : ''}`}
										key={data.data}
									>
										<PhoneContactIcon className="info-tab-contact-icon info-tab-contact-icon-phone" />

										<a
											className="info-tab-contact-content"
											href={`tel:${data.data}`}
											target="_blank"
											onClick={() => {
												toggleContactedUserData(ind);
											}}
											rel="noopener noreferrer"
										>
											<div>{data.data}</div>
										</a>

										<Trash
											id="info-tab-phone-trash"
											className="info-tab-contact-trash cursor-pointer"
											onClick={() => {
												openDeleteDataModal(ind);
											}}
										/>

										<div className="my-4 ml-4 inline-block border-2 border-transparent">
											<CopyToClipboard
												textToCopy={data.data}
												type="Phone number"
											/>
										</div>

										<button
											type="button"
											className="info-tab-contacted-switch"
											onClick={() => {
												toggleContactedUserData(ind);
											}}
										>
											<Checkmark className="info-tab-contact-check" />{' '}
											<span className="info-tab-contact-checked">
												Contacted
											</span>
											<span className="info-tab-contact-not-checked">
												Mark contacted
											</span>
										</button>
									</div>
								);
							})}

						<button
							type="button"
							className="info-tab-contact-type-holder cursor-pointer"
							onClick={openAddDataModal}
						>
							<PlusGold className="info-tab-add-data-icon" />
							<div
								className="info-tab-contact-add-data"
								id="info-tab-add-data"
							>
								Add Contact Data
							</div>
						</button>

						<button
							type="button"
							className="info-tab-contact-type-holder cursor-pointer"
							onClick={() =>
								setIsReportDataModalOpen(state => !state)
							}
						>
							<ExclamationMarkOctagon className="info-tab-report-data-icon" />
							<div
								className="info-tab-contact-report-data"
								id="info-tab-report-data"
							>
								Report Incorrect Data
							</div>
						</button>
					</div>
				</div>

				<div className="info-tab-address-holder">
					{selectBusiness.address &&
						selectBusiness.address.address && (
							<>
								<div className="info-tab-section-title">
									Address
								</div>

								<div className="info-tab-address-stats">
									{selectBusiness.address.address}
									{user?.features?.platformRedesign &&
										query?.coordinates &&
										parentNodeName ===
											'search-businesses' && (
											<Button
												variant="secondary"
												card
												className="address-feature-button"
												leftIcon={<Map />}
												onClick={() =>
													goToMap?.(
														selectBusiness?.id ||
															selectBusiness._id
													)
												}
											>
												Show on map
											</Button>
										)}
								</div>
							</>
						)}

					{!selectBusiness.hours ||
					(!selectBusiness.hours.M &&
						!selectBusiness.hours.T &&
						!selectBusiness.hours.W &&
						!selectBusiness.hours.Th &&
						!selectBusiness.hours.F &&
						!selectBusiness.hours.S &&
						!selectBusiness.hours.Su) ? (
						''
					) : (
						<>
							<button
								type="button"
								className="info-tab-address-open"
								onClick={toggleHoursDropdown}
							>
								<span>See all location hours</span>{' '}
								<DropDownGold className="down-chevron" />
							</button>

							<div className="hours-holder">
								{selectBusiness.hours.M && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Monday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.M}
										</span>
									</div>
								)}

								{selectBusiness.hours.T && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Tuesday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.T}
										</span>
									</div>
								)}

								{selectBusiness.hours.W && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Wednesday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.W}
										</span>
									</div>
								)}

								{selectBusiness.hours.Th && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Thursday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.Th}
										</span>
									</div>
								)}

								{selectBusiness.hours.F && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Friday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.F}
										</span>
									</div>
								)}

								{selectBusiness.hours.S && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Saturday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.S}
										</span>
									</div>
								)}

								{selectBusiness.hours.Sa && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Saturday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.Sa}
										</span>
									</div>
								)}

								{selectBusiness.hours.Su && (
									<div className="info-tab-address-hours">
										<span className="hours-label">
											Sunday
										</span>{' '}
										<span className="hours-text">
											{selectBusiness.hours.Su}
										</span>
									</div>
								)}
							</div>
						</>
					)}
				</div>

				{selectBusiness &&
					selectBusiness.yelp &&
					selectBusiness.yelp.about && (
						<div className="info-tab-about-holder">
							<div className="info-tab-section-title">About</div>

							<>
								<div
									className={`info-tab-section-about ${showAboutMore ? 'info-tab-about-full' : ''}`}
								>
									{selectBusiness.yelp.about}
								</div>

								{selectBusiness.yelp.about.length > 115 && (
									<button
										type="button"
										className="info-tab-about-more"
										onClick={toggleAbout}
									>
										{!showAboutMore
											? '...show more'
											: 'show less'}
									</button>
								)}
							</>
						</div>
					)}

				<div className="info-tab-categories-holder">
					{selectBusiness.categories &&
						selectBusiness.categories.search && (
							<>
								<div className="info-tab-section-title">
									Categories
								</div>

								{selectBusiness.categories.search.map(
									category => (
										<div
											className="single-category-pill"
											key={category}
										>
											{category.replace(/&apos;/g, "'")}
										</div>
									)
								)}
							</>
						)}
				</div>

				{selectBusiness?.yearsInBusiness &&
					!selectBusiness?.yearIncorporated && (
						<div className="info-tab-categories-holder">
							<div className="info-tab-section-title">
								Year Incorporated
							</div>
							<div className="info-tab-point-contact">
								{getDateIncorporated(
									selectBusiness.yearsInBusiness
								)}
							</div>
						</div>
					)}

				{selectBusiness?.yearIncorporated && (
					<div className="info-tab-categories-holder">
						<div className="info-tab-section-title">
							Year Incorporated
						</div>
						<div className="info-tab-point-contact">
							{selectBusiness.yearIncorporated}
						</div>
					</div>
				)}

				{selectBusiness?.customData?.numEmployees && (
					<div className="info-tab-categories-holder">
						<div className="info-tab-sales-number-employees">
							<div className="info-tab-section-title flex gap-2">
								Number of Employees
								<div className="info-white-wrapper">
									<InfoWhite />
									<span className="info-tab-point-contact-hovertext">
										Information reported on most recent
										public corporate filings or estimated
										based on Resquared models otherwise.
									</span>
								</div>
							</div>
							<div className="info-tab-point-contact">
								{selectBusiness.customData.numEmployees}
							</div>
						</div>
					</div>
				)}

				{selectBusiness?.customData?.salesVolume && (
					<div className="info-tab-categories-holder">
						<div className="info-tab-sales-volume">
							<div className="info-tab-section-title flex gap-2">
								Sales Volume
								<div className="info-white-wrapper">
									<InfoWhite />
									<span className="info-tab-point-contact-hovertext">
										Information reported on most recent
										public corporate filings or estimated
										based on Resquared models otherwise.
									</span>
								</div>
							</div>
							<div className="info-tab-point-contact">
								{selectBusiness.customData.salesVolume}
							</div>
						</div>
					</div>
				)}

				{selectBusiness?.customData?.sqftRangeEstimateMax &&
					selectBusiness?.customData?.sqftRangeEstimateMin && (
						<div className="info-tab-categories-holder">
							<div className="info-tab-sqft-estimate">
								<div className="info-tab-section-title flex gap-2">
									Square Feet
									<div className="info-white-wrapper">
										<InfoWhite />
										<span className="info-tab-point-contact-hovertext-sqft-estimate">
											{
												selectBusiness?.customData
													?.sqftRangeEstimateReasoning
											}
										</span>
									</div>
								</div>

								<div className="info-tab-point-contact">
									{
										selectBusiness?.customData
											?.sqftRangeEstimateMin
									}{' '}
									sq. ft -{' '}
									{
										selectBusiness?.customData
											?.sqftRangeEstimateMax
									}{' '}
									sq. ft
								</div>
							</div>
						</div>
					)}

				{selectBusiness?.contactNames &&
					selectBusiness?.contactNames?.length > 0 && (
						<div className="info-tab-points-contact-holder">
							<div className="info-tab-section-title">
								Staff Info
							</div>

							{selectBusiness?.contactNames?.map(contactInfo => {
								const titles = contactTitleCleanUp(
									contactInfo.titles
								);

								if (titles.length >= 1) {
									return (
										<div
											className="info-tab-point-contact"
											key={contactInfo.name}
										>
											{`${contactNameCleanUp(contactInfo.name)}, `}
											<span>
												{' '}
												{titles.map(title => title)}
											</span>
										</div>
									);
								}

								return (
									<div
										className="info-tab-point-contact"
										key={contactInfo.name}
									>
										{contactNameCleanUp(contactInfo.name)}
									</div>
								);
							})}

							{selectBusiness.businessBureau &&
								selectBusiness.businessBureau
									.businessManagersInfo &&
								selectBusiness.businessBureau
									.businessManagersInfo.length > 0 &&
								selectBusiness.businessBureau.businessManagersInfo.map(
									contact => (
										<div
											className="info-tab-point-contact"
											key={contact}
										>
											{contact}
										</div>
									)
								)}
						</div>
					)}

				<div className="info-tab-online-presence-holder">
					<div className="info-tab-section-title">
						Online Presence
					</div>

					{selectBusiness &&
						selectBusiness.website &&
						selectBusiness.website !== 'false' &&
						!selectBusiness.website && (
							<div className="info-tab-point-contact-row">
								<a
									href={encodeUrl(
										selectBusiness.website.startsWith(
											'http://'
										) ||
											selectBusiness.website.startsWith(
												'https://'
											)
											? selectBusiness.website
											: `http://${selectBusiness.website}`
									)}
									className="info-tab-online-link"
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										addLinkAnalytics('WEBSITE');
										saveNote?.(
											selectBusiness.businessId ||
												(selectBusiness._id as string),
											{
												contactType: {
													...selectBusiness.contactType,
													website: true
												},
												isContacted:
													selectBusiness.isContacted,
												lead: selectBusiness?.lead,
												note:
													selectBusiness?.note ?? '',
												userAddedData:
													selectBusiness.userAddedData
											} as UserNotes
										);
									}}
								>
									<Website className="info-tab-link-icon" />

									<span>
										{selectBusiness.website.startsWith(
											'http://'
										) ||
										selectBusiness.website.startsWith(
											'https://'
										)
											? selectBusiness.website
											: `http://${selectBusiness.website}`}
									</span>
								</a>
							</div>
						)}

					{selectBusiness && selectBusiness?.websites?.length > 0 && (
						<div className="info-tab-point-contact-row">
							{Object.keys(selectBusiness.websites).map(
								websiteKey => (
									<a
										key={`${websiteKey}_url`}
										href={encodeUrl(
											selectBusiness.websites[
												websiteKey
											].startsWith('http://') ||
												selectBusiness.websites[
													websiteKey
												].startsWith('https://')
												? selectBusiness.websites[
														websiteKey
													]
												: `http://${selectBusiness.websites[websiteKey]}`
										)}
										className="info-tab-online-link"
										target="_blank"
										rel="noopener noreferrer"
										onClick={() => {
											addLinkAnalytics('WEBSITE');
											saveNote?.(
												selectBusiness.businessId ||
													(selectBusiness._id as string),
												{
													contactType: {
														...selectBusiness.contactType,
														website: true
													},
													isContacted:
														selectBusiness.isContacted,
													lead: selectBusiness?.lead,
													note:
														selectBusiness?.note ??
														'',
													userAddedData:
														selectBusiness.userAddedData
												} as UserNotes
											);
										}}
									>
										<Website className="info-tab-link-icon" />

										<span key={`${websiteKey}_label`}>
											{selectBusiness.websites[
												websiteKey
											].startsWith('http://') ||
											selectBusiness.websites[
												websiteKey
											].startsWith('https://')
												? selectBusiness.websites[
														websiteKey
													]
												: `http://${selectBusiness.websites[websiteKey]}`}
										</span>
									</a>
								)
							)}
						</div>
					)}

					{selectBusiness?.facebook?.website &&
						selectBusiness.facebook?.website !== 'false' &&
						!selectBusiness.facebook?.website && (
							<div className="info-tab-point-contact-row">
								<a
									href={encodeUrl(
										selectBusiness.facebook.website.startsWith(
											'http://'
										) ||
											selectBusiness.facebook.website.startsWith(
												'https://'
											)
											? selectBusiness.facebook.website
											: `http://${selectBusiness.facebook.website}`
									)}
									className="info-tab-online-link"
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										addLinkAnalytics('WEBSITE');
									}}
								>
									<Website className="info-tab-link-icon" />
									<span>
										{selectBusiness.facebook.website.startsWith(
											'http://'
										) ||
										selectBusiness.facebook.website.startsWith(
											'https://'
										)
											? selectBusiness.facebook.website
											: `http://${selectBusiness.facebook.website}`}
									</span>
								</a>
							</div>
						)}

					{selectBusiness && (
						<div className="info-tab-point-contact-row">
							<a
								href={encodeUrl(
									`https://google.com/search?q=${searchTerm()}`
								)}
								className="info-tab-online-link"
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('WEBSITE');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												google: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<Google className="info-tab-link-icon" />
								<span>Google Search</span>
							</a>
						</div>
					)}

					{selectBusiness &&
						selectBusiness.userAddedData &&
						selectBusiness.userAddedData.map((data, ind) => {
							if (data.type !== 'web-link') return null;

							return (
								<div
									className="info-tab-point-contact-row"
									key={data.data}
								>
									<a
										href={encodeUrl(
											data.linkData.indexOf('http') ===
												-1 ||
												data.linkData.indexOf(
													'https'
												) === -1
												? `https://${data.linkData}`
												: data.linkData
										)}
										className="info-tab-online-link"
										rel="noopener noreferrer"
										target="_blank"
										onClick={() => {
											addLinkAnalytics('WEBSITE');
										}}
									>
										<Website className="info-tab-link-icon" />
										<span>
											{data.data.startsWith('http://') ||
											data.data.startsWith('https://')
												? data.data
												: `http://${data.data}`}
										</span>
									</a>
									<Trash
										id="info-tab-web-trash"
										className="info-tab-site-trash cursor-pointer"
										onClick={() => {
											openDeleteDataModal(ind);
										}}
									/>
								</div>
							);
						})}

					{selectBusiness &&
						selectBusiness.yelp &&
						selectBusiness.yelp.url && (
							<div className="info-tab-point-contact-row">
								<a
									href={selectBusiness.yelp.url}
									target="_blank"
									rel="noopener noreferrer"
									className="info-tab-online-link"
									onClick={() => {
										addLinkAnalytics('YELP');
										saveNote?.(
											selectBusiness.businessId ||
												(selectBusiness._id as string),
											{
												contactType: {
													...selectBusiness.contactType,
													yelp: true
												},
												isContacted:
													selectBusiness.isContacted,
												lead: selectBusiness?.lead,
												note:
													selectBusiness?.note ?? '',
												userAddedData:
													selectBusiness.userAddedData
											} as UserNotes
										);
									}}
								>
									<Yelp className="info-tab-link-icon" />
									<span>Yelp</span>
								</a>
							</div>
						)}

					{selectBusiness &&
						selectBusiness.facebook &&
						selectBusiness.facebook.url && (
							<div className="info-tab-point-contact-row">
								<a
									href={selectBusiness.facebook.url}
									target="_blank"
									rel="noopener noreferrer"
									className="info-tab-online-link"
									onClick={() => {
										addLinkAnalytics('FACEBOOKWEBSITE');
										saveNote?.(
											selectBusiness.businessId ||
												(selectBusiness._id as string),
											{
												contactType: {
													...selectBusiness.contactType,
													facebook: true
												},
												isContacted:
													selectBusiness.isContacted,
												lead: selectBusiness?.lead,
												note:
													selectBusiness?.note ?? '',
												userAddedData:
													selectBusiness.userAddedData
											} as UserNotes
										);
									}}
								>
									<Messenger className="info-tab-link-icon info-tab-link-icon-messenger" />
									<span>Facebook</span>
								</a>
							</div>
						)}

					{selectBusiness &&
						selectBusiness.address?.city &&
						(selectBusiness?.socials?.instagram ? (
							<div className="info-tab-point-contact-row">
								<a
									target="_blank"
									href={selectBusiness?.socials?.instagram}
									className="info-tab-online-link"
									rel="noopener noreferrer"
									onClick={() => {
										addLinkAnalytics('INSTAGRAM');
										saveNote?.(
											selectBusiness.businessId ||
												(selectBusiness._id as string),
											{
												contactType: {
													...selectBusiness.contactType,
													instagram: true
												},
												isContacted:
													selectBusiness.isContacted,
												lead: selectBusiness?.lead,
												note:
													selectBusiness?.note ?? '',
												userAddedData:
													selectBusiness.userAddedData
											} as UserNotes
										);
									}}
								>
									<Instagram className="info-tab-link-icon" />
									<span>Instagram</span>
								</a>
							</div>
						) : (
							<div className="info-tab-point-contact-row">
								<a
									target="_blank"
									href={`https://www.google.com/search?q=${encodeURIComponent(
										selectBusiness.address?.city as string
									)}+${encodeURIComponent(selectBusiness.name as string)}+instagram
							`}
									className="info-tab-online-link"
									rel="noopener noreferrer"
									onClick={() => {
										addLinkAnalytics('INSTAGRAM');
										saveNote?.(
											selectBusiness.businessId ||
												(selectBusiness._id as string),
											{
												contactType: {
													...selectBusiness.contactType,
													instagram: true
												},
												isContacted:
													selectBusiness.isContacted,
												lead: selectBusiness?.lead,
												note:
													selectBusiness?.note ?? '',
												userAddedData:
													selectBusiness.userAddedData
											} as UserNotes
										);
									}}
								>
									<Instagram className="info-tab-link-icon" />
									<span>Find on Instagram</span>
								</a>
							</div>
						))}

					{/* TWITTER */}
					{selectBusiness?.socials?.twitter && (
						<div className="info-tab-point-contact-row">
							<a
								target="_blank"
								href={selectBusiness?.socials?.twitter}
								className="info-tab-online-link"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('TWITTER');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												twitter: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<img
									src={Twitter}
									className="info-tab-link-icon twitter"
									alt="twitter icon"
								/>
								<span>Find on Twitter</span>
							</a>
						</div>
					)}

					{/* TIKTOK */}
					{selectBusiness?.socials?.tiktok && (
						<div className="info-tab-point-contact-row">
							<a
								target="_blank"
								href={selectBusiness?.socials?.tiktok}
								className="info-tab-online-link"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('TIKTOK');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												tiktok: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<img
									src={Tiktok}
									className="info-tab-link-icon tiktok"
									alt="tiktok icon"
								/>
								<span>Find on TikTok</span>
							</a>
						</div>
					)}

					{/* SNAPCHAT */}
					{selectBusiness?.socials?.snapchat && (
						<div className="info-tab-point-contact-row">
							<a
								target="_blank"
								href={selectBusiness?.socials?.snapchat}
								className="info-tab-online-link"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('SNAPCHAT');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												snapchat: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<img
									src={Snapchat}
									className="info-tab-link-icon snapchat"
									alt="snapchat icon"
								/>
								<span>Find on Snapchat</span>
							</a>
						</div>
					)}

					{/* YOUTUBE */}
					{selectBusiness?.socials?.youtube && (
						<div className="info-tab-point-contact-row">
							<a
								target="_blank"
								href={selectBusiness?.socials?.youtube}
								className="info-tab-online-link"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('YOUTUBE');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												youtube: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<img
									src={Youtube}
									className="info-tab-link-icon youtube"
									alt="youtube icon"
								/>
								<span>Find on Youtube</span>
							</a>
						</div>
					)}

					{/* PINTEREST */}
					{selectBusiness?.socials?.pinterest && (
						<div className="info-tab-point-contact-row">
							<a
								target="_blank"
								href={selectBusiness?.socials?.pinterest}
								className="info-tab-online-link"
								rel="noopener noreferrer"
								onClick={() => {
									addLinkAnalytics('PINTEREST');
									saveNote?.(
										selectBusiness.businessId ||
											(selectBusiness._id as string),
										{
											contactType: {
												...selectBusiness.contactType,
												pinterest: true
											},
											isContacted:
												selectBusiness.isContacted,
											lead: selectBusiness?.lead,
											note: selectBusiness?.note ?? '',
											userAddedData:
												selectBusiness.userAddedData
										} as UserNotes
									);
								}}
							>
								<img
									src={Pinterest}
									className="info-tab-link-icon pinterest"
									alt="pinterest icon"
								/>
								<span>Find on Pinterest</span>
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default BusinessInfoTab;
